import {
  Alert,
  AlertDescription,
  AlertTitle,
  Badge,
  Button,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Skeleton,
  Spinner,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from "@veritrellis/ui";
import { AlertTriangle, ChevronRight, FlaskConical } from "lucide-react";
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { apiFetch } from "../../lib/api.js";
import { envLabel } from "../../lib/requestPresentation.js";
import {
  formatSimulationDecision,
  simulationDecisionBadgeVariant,
  type SimulationSummary
} from "../../lib/policySimulation.js";

const actionTypes = [
  "issue_refund",
  "issue_credit",
  "billing.credit.issue",
  "contract.commitment.send",
  "change_plan_or_price",
  "cancel_or_delete_account",
  "send_external_message"
] as const;

type PolicySimulationPanelProps = {
  workspaceId: string;
  actionType: string;
  environment: "sandbox" | "production";
  draftJson: string;
  onActionTypeChange?: (actionType: string) => void;
  onEnvironmentChange?: (env: "sandbox" | "production") => void;
  editPolicyHref?: string;
  showConfig?: boolean;
};

export const PolicySimulationPanel = ({
  workspaceId,
  actionType,
  environment,
  draftJson,
  onActionTypeChange,
  onEnvironmentChange,
  editPolicyHref,
  showConfig = false
}: PolicySimulationPanelProps) => {
  const [result, setResult] = useState<SimulationSummary | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [flipsOnly, setFlipsOnly] = useState(false);

  const runSimulation = async () => {
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      const draftPolicy = JSON.parse(draftJson) as unknown;
      const res = await apiFetch(`/admin/workspaces/${workspaceId}/policies/simulate`, {
        method: "POST",
        body: JSON.stringify({ action_type: actionType, environment, draft_policy: draftPolicy })
      });
      if (!res.ok) {
        const body = (await res.json()) as { error?: { message?: string } };
        setError(body?.error?.message ?? "simulation_failed");
      } else {
        const body = (await res.json()) as { simulation: SimulationSummary };
        setResult(body.simulation);
        if (body.simulation.decisionFlips > 0) {
          setFlipsOnly(true);
        }
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "unexpected_error");
    } finally {
      setLoading(false);
    }
  };

  const visibleRecords = useMemo(() => {
    if (result === null) {
      return [];
    }
    return flipsOnly ? result.records.filter((r) => r.decisionChanged) : result.records;
  }, [result, flipsOnly]);

  const statDecisions = ["allow", "deny", "require_approval", "modify"] as const;

  return (
    <div className="admin-simulation">
      {showConfig ? (
        <section className="admin-panel" aria-labelledby="sim-config-heading">
          <div className="admin-panel-header">
            <h2 id="sim-config-heading" className="admin-panel-title">
              Simulation target
            </h2>
          </div>
          <div className="admin-panel-body-padded">
            <div className="admin-sim-config-grid">
              <div className="admin-form-field admin-form-field-full">
                <label className="admin-form-field-label">Action type</label>
                <div className="admin-form-field-control">
                  <Select
                    value={actionType}
                    onValueChange={(v) => onActionTypeChange?.(v)}
                    disabled={onActionTypeChange === undefined}
                  >
                    <SelectTrigger style={{ height: 36, width: "100%" }}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {actionTypes.map((t) => (
                        <SelectItem key={t} value={t}>
                          {t}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="admin-form-field admin-form-field-full">
                <label className="admin-form-field-label">Environment</label>
                <div className="admin-form-field-control">
                  <Select
                    value={environment}
                    onValueChange={(v) => {
                      if (v === "sandbox" || v === "production") {
                        onEnvironmentChange?.(v);
                      }
                    }}
                    disabled={onEnvironmentChange === undefined}
                  >
                    <SelectTrigger style={{ height: 36, width: "100%" }}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="sandbox">Sandbox</SelectItem>
                      <SelectItem value="production">Production</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          </div>
        </section>
      ) : null}

      <section className="admin-panel admin-sim-run-panel" aria-labelledby="sim-run-heading">
        <div className="admin-panel-header">
          <h2 id="sim-run-heading" className="admin-panel-title">
            Test impact
          </h2>
          {editPolicyHref != null ? (
            <Link to={editPolicyHref} className="admin-panel-link">
              Edit policy
            </Link>
          ) : null}
        </div>
        <div className="admin-panel-body-padded">
          <div className="admin-sim-intro">
            <div className="admin-sim-intro-icon" aria-hidden>
              <FlaskConical style={{ width: 20, height: 20 }} />
            </div>
            <div>
              <p className="admin-sim-intro-title">Replay against recent traffic</p>
              <p className="admin-form-field-hint">
                Compares your <strong style={{ color: "var(--foreground)" }}>draft policy</strong>{" "}
                to the <strong style={{ color: "var(--foreground)" }}>stored policy</strong> on up
                to 200 historical <code style={{ fontSize: 11 }}>{actionType}</code> requests in{" "}
                {envLabel(environment)}.
              </p>
            </div>
          </div>
          <div className="admin-sim-actions">
            <Button disabled={loading} onClick={() => void runSimulation()}>
              {loading ? <Spinner style={{ width: 16, height: 16, marginRight: 8 }} /> : null}
              Run simulation
            </Button>
          </div>
        </div>
      </section>

      {error != null ? (
        <Alert variant="destructive">
          <AlertTitle>Simulation failed</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      ) : null}

      {loading && result === null ? (
        <section className="admin-panel">
          <div className="admin-panel-body-padded">
            <Skeleton style={{ height: 72, width: "100%", marginBottom: 12 }} />
            <Skeleton style={{ height: 240, width: "100%" }} />
          </div>
        </section>
      ) : null}

      {result != null ? (
        <>
          <section className="admin-sim-stats" aria-label="Simulation summary">
            <article className="admin-sim-stat-card">
              <p className="admin-sim-stat-label">Evaluated</p>
              <p className="admin-sim-stat-value">{result.totalEvaluated}</p>
            </article>
            <article
              className={`admin-sim-stat-card${result.decisionFlips > 0 ? " admin-sim-stat-card-warn" : ""}`}
            >
              <p className="admin-sim-stat-label">Decision flips</p>
              <p className="admin-sim-stat-value">{result.decisionFlips}</p>
            </article>
            {statDecisions.map((d) => (
              <article key={d} className="admin-sim-stat-card admin-sim-stat-card-muted">
                <p className="admin-sim-stat-label">{formatSimulationDecision(d)}</p>
                <p className="admin-sim-stat-value">{result.byDraftDecision[d] ?? 0}</p>
              </article>
            ))}
          </section>

          {result.decisionFlips > 0 ? (
            <Alert className="border-amber-200 bg-amber-50">
              <AlertTriangle
                style={{ width: 16, height: 16, flexShrink: 0, color: "#b45309" }}
                aria-hidden
              />
              <AlertTitle>{result.decisionFlips} requests would change decision</AlertTitle>
              <AlertDescription>
                Review flipped rows before saving to production. Unchanged requests are hidden when
                &quot;Flips only&quot; is on.
              </AlertDescription>
            </Alert>
          ) : (
            <Alert className="border-green-200 bg-green-50">
              <AlertTitle>No decision flips</AlertTitle>
              <AlertDescription>
                Draft policy matches stored policy outcomes for all evaluated requests.
              </AlertDescription>
            </Alert>
          )}

          <section className="admin-panel" aria-labelledby="sim-results-heading">
            <div className="admin-panel-header">
              <h2 id="sim-results-heading" className="admin-panel-title">
                Results
              </h2>
              <button
                type="button"
                className={`admin-filter-pill${flipsOnly ? " admin-filter-pill-active" : ""}`}
                onClick={() => setFlipsOnly((v) => !v)}
                aria-pressed={flipsOnly}
              >
                Flips only
              </button>
            </div>

            {visibleRecords.length === 0 ? (
              <div className="admin-panel-body-padded">
                <p className="admin-empty-state-text" style={{ margin: 0 }}>
                  {flipsOnly
                    ? "No decision flips — try turning off “Flips only”."
                    : "No historical requests to evaluate."}
                </p>
              </div>
            ) : (
              <div className="admin-panel-body">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead style={{ paddingLeft: 20 }}>Request</TableHead>
                      <TableHead>Stored</TableHead>
                      <TableHead>Draft</TableHead>
                      <TableHead>Reason</TableHead>
                      <TableHead style={{ textAlign: "right", paddingRight: 20 }} />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {visibleRecords.map((r) => (
                      <TableRow
                        key={r.requestId}
                        className={r.decisionChanged ? "admin-sim-row-flipped" : undefined}
                      >
                        <TableCell style={{ paddingLeft: 20 }}>
                          <code style={{ fontSize: 11, color: "var(--muted-foreground)" }}>
                            {r.requestId.slice(0, 10)}…
                          </code>
                        </TableCell>
                        <TableCell>
                          <Badge variant={simulationDecisionBadgeVariant(r.liveDecision)}>
                            {formatSimulationDecision(r.liveDecision)}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Badge variant={simulationDecisionBadgeVariant(r.draftDecision)}>
                            {formatSimulationDecision(r.draftDecision)}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <code style={{ fontSize: 11 }}>{r.draftReasonCode}</code>
                        </TableCell>
                        <TableCell style={{ textAlign: "right", paddingRight: 20 }}>
                          {r.decisionChanged ? (
                            <Badge variant="warning">Flipped</Badge>
                          ) : (
                            <span style={{ fontSize: 11, color: "var(--muted-foreground)" }}>
                              —
                            </span>
                          )}
                          <Link
                            to={`/requests/${r.requestId}`}
                            className="admin-sim-row-link"
                            style={{ marginLeft: 8 }}
                          >
                            View
                            <ChevronRight style={{ width: 12, height: 12 }} />
                          </Link>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </section>
        </>
      ) : null}
    </div>
  );
};
